import axios from "api/axios";

export interface BulkAssignError {
  employee_id: number;
  detail: string;
}

export interface BulkAssignResult {
  assigned: unknown[];
  errors: BulkAssignError[];
}

export type AssignmentStatus =
  | "new"
  | "analyzing"
  | "recommended"
  | "assigned"
  | "employee_checking"
  | "eligible"
  | "not_eligible"
  | "proposal_prepared"
  | "applied_manually"
  | "interviewing"
  | "hired"
  | "rejected"
  | "skipped"
  | "expired";

export interface UpworkJob {
  id: number;
  ciphertext: string;
  upwork_job_id?: string | null;
  title: string;
  description?: string | null;
  job_url: string;
  search_profile: string;
  category?: string | null;
  subcategory?: string | null;
  job_type?: string | null;
  experience_level?: string | null;
  duration?: string | null;
  duration_label?: string | null;
  amount_raw_value?: number | null;
  amount_currency?: string | null;
  amount_display_value?: string | null;
  skills?: string | null;
  client_info?: string | null;
  total_applicants?: number | null;
  freelancers_to_hire?: number | null;
  published_date_time?: string | null;
  created_date_time?: string | null;
  is_active: boolean;
  discovered_at?: string | null;
  updated_at?: string | null;
}

export interface UpworkAssignment extends Omit<UpworkJob, "description"> {
  // Slim job fields (description/raw_job excluded in list views, same as TheirStack)
  description?: null;

  // Assignment overlay
  assignment_id: number;
  assigned_by: "auto" | "admin";
  status: AssignmentStatus;
  assignment_notes?: string | null;
  assignment_from_status?: string | null;
  assignment_created_at?: string | null;
  assignment_updated_at?: string | null;
  employee_id?: number; // admin view only
}

export interface PagedResult<T> {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: T[];
}

export interface AssignmentHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export interface JobFilters {
  page?: number;
  page_size?: number;
  search_profile?: string;
  experience_level?: string;
  search?: string;
  active_only?: boolean;
  unassigned_only?: boolean;
}

export interface AssignmentFilters {
  page?: number;
  page_size?: number;
  employee_id?: number;
  status_filter?: string;
  hide_archived?: boolean;
  search_profile?: string;
  experience_level?: string;
  search?: string;
}

export const STATUS_OPTIONS: { value: AssignmentStatus; label: string }[] = [
  { value: "new",               label: "New" },
  { value: "analyzing",         label: "Analyzing" },
  { value: "recommended",       label: "Recommended" },
  { value: "assigned",          label: "Assigned" },
  { value: "employee_checking", label: "Employee Checking" },
  { value: "eligible",          label: "Eligible" },
  { value: "not_eligible",      label: "Not Eligible" },
  { value: "proposal_prepared", label: "Proposal Prepared" },
  { value: "applied_manually",  label: "Applied Manually" },
  { value: "interviewing",      label: "Interviewing" },
  { value: "hired",             label: "Hired" },
  { value: "rejected",          label: "Rejected" },
  { value: "skipped",           label: "Skipped" },
  { value: "expired",           label: "Expired" },
];

/** Statuses the backend enforces as terminal — a further status update past this
 * point is rejected (422), so the UI must not offer it. */
export const TERMINAL = new Set<AssignmentStatus>(["hired", "rejected", "not_eligible", "skipped", "expired"]);

const ADMIN = "/admin/upwork-jobs";
const EMP   = "/employee/upwork-jobs";
const base  = (isAdmin: boolean) => (isAdmin ? ADMIN : EMP);

function clean(filters: object) {
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));
}

export async function fetchUpworkJobs(filters: JobFilters = {}): Promise<PagedResult<UpworkJob>> {
  const res = await axios.get(ADMIN, { params: clean(filters) });
  return res.data;
}

export async function assignJob(jobId: number, employeeIds: number[]): Promise<BulkAssignResult> {
  const res = await axios.post(`${ADMIN}/${jobId}/assign`, { employee_ids: employeeIds });
  return res.data;
}

export async function unassignJob(jobId: number, employeeId: number): Promise<void> {
  await axios.delete(`${ADMIN}/${jobId}/assign/${employeeId}`);
}

export interface ReassignedAssignment {
  id: number;
  employee_id: number;
  assigned_by: "auto" | "admin";
}

export async function reassignAssignment(assignmentId: number, employeeId: number): Promise<ReassignedAssignment> {
  const res = await axios.patch(`${ADMIN}/assignments/${assignmentId}/reassign`, { employee_id: employeeId });
  return res.data;
}

export interface JobAssignee {
  employee_id: number;
  employee_code: string;
  name: string;
  status: AssignmentStatus;
  assigned_by: "auto" | "admin";
}

export async function fetchJobAssignees(jobId: number): Promise<JobAssignee[]> {
  const res = await axios.get(`${ADMIN}/${jobId}/assignees`);
  return res.data;
}

export async function fetchAssignmentStatusCounts(
  isAdmin: boolean,
  employeeId?: number,
): Promise<Record<string, number>> {
  const params = isAdmin && employeeId ? { employee_id: employeeId } : {};
  const res = await axios.get(`${base(isAdmin)}/assignments/status-counts`, { params });
  return res.data;
}

export async function fetchAssignments(
  filters: AssignmentFilters = {},
  isAdmin = false,
): Promise<PagedResult<UpworkAssignment>> {
  const res = await axios.get(`${base(isAdmin)}/assignments`, { params: clean(filters) });
  return res.data;
}

export async function fetchAssignmentDetail(
  assignmentId: number,
  isAdmin: boolean,
): Promise<UpworkAssignment> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}`);
  return res.data;
}

export async function fetchAssignmentHistory(
  assignmentId: number,
  isAdmin: boolean,
): Promise<AssignmentHistoryEntry[]> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}/history`);
  return res.data;
}

export async function updateAssignmentStatus(
  assignmentId: number,
  status: AssignmentStatus,
  notes: string | null,
  isAdmin: boolean,
): Promise<void> {
  await axios.patch(`${base(isAdmin)}/assignments/${assignmentId}/status`, { status, notes });
}

export interface SyncResult {
  profiles_run: number;
  inserted: number;
  updated: number;
  skipped: number;
  auto_assigned: number;
  timed_out?: boolean;
  error?: string;
  profile_errors?: { profile: string; error: string }[];
}

export async function syncUpworkJobsNow(): Promise<SyncResult> {
  const res = await axios.post(`${ADMIN}/sync-now`);
  return res.data;
}

// ── Old-jobs cleanup — jobs are never auto-deleted; this is an explicit,
// admin-triggered, preview-then-confirm action, mirroring LinkedIn Jobs' own
// "Delete Old Jobs" cleanup. ──────────────────────────────────────────────────

export interface OldJobsCleanupPreview {
  job_count: number;
  assignment_count: number;
  history_count: number;
  cutoff_iso: string;
}

export interface OldJobsCleanupResult {
  deleted_jobs: number;
  deleted_assignments: number;
  deleted_histories: number;
}

export async function fetchOldJobsCleanupPreview(months: number): Promise<OldJobsCleanupPreview> {
  const res = await axios.get(`${ADMIN}/cleanup/old-jobs-preview`, { params: { months } });
  return res.data;
}

export async function executeOldJobsCleanup(months: number, cutoffIso?: string): Promise<OldJobsCleanupResult> {
  const params: Record<string, string | number> = { months };
  if (cutoffIso) params.cutoff_iso = cutoffIso;
  const res = await axios.delete(`${ADMIN}/cleanup/old-jobs`, { params });
  return res.data;
}
